import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import {
  Badge,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { DataTable } from "../DataTable";
import { ActivityDate } from "../ActivityDate";
import type { FirmwareVersionDTO } from "../../utils/api";

interface FirmwareVersionsTableProps {
  versions: FirmwareVersionDTO[];
  totalVersions: number;
  // eslint-disable-next-line no-unused-vars
  onEdit?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onPublish?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDownload?: (version: FirmwareVersionDTO) => void;
  onTableChange: (
    // eslint-disable-next-line no-unused-vars
    pagination: Updater<PaginationState>,
    // eslint-disable-next-line no-unused-vars
    sorting: Updater<SortingState>
  ) => void;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
  showActions?: boolean;
}

interface FirmwareVersionsTableMeta {
  // eslint-disable-next-line no-unused-vars
  onEdit?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onPublish?: (version: FirmwareVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDownload?: (version: FirmwareVersionDTO) => void;
  latestPublishedVersionCode?: number;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const actionsColumn: ColumnDef<FirmwareVersionDTO, unknown> = {
  id: "actions",
  header: "Actions",
  enableSorting: false,
  enableResizing: true,
  cell: ({ row, table }) => {
    const version = row.original;
    const meta = table.options.meta as FirmwareVersionsTableMeta;
    return (
      <ButtonGroup
        className="d-block mx-auto"
        style={{
          marginTop: "-6px",
          marginBottom: "-6px",
        }}
      >
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`edit-firmware-${version.id}`}>Edit</Tooltip>}
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Edit firmware version"
              onClick={() => meta?.onEdit?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faPencil} />
            </Button>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`publish-firmware-${version.id}`}>Publish</Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Publish firmware version"
              disabled={version.status === "PUBLISHED"}
              style={
                version.status === "PUBLISHED"
                  ? { pointerEvents: "none" }
                  : undefined
              }
              onClick={() => meta?.onPublish?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faUpload} />
            </Button>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`delete-firmware-${version.id}`}>Delete</Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Delete firmware version"
              onClick={() => meta?.onDelete?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faTrash} />
            </Button>
          </span>
        </OverlayTrigger>
      </ButtonGroup>
    );
  },
  meta: {
    width: "min-content",
  },
};

const baseColumns: ColumnDef<FirmwareVersionDTO, unknown>[] = [
  {
    accessorKey: "versionName",
    header: () => "Version Name",
    cell: ({ row, table }) => {
      const version = row.original;
      const meta = table.options.meta as FirmwareVersionsTableMeta;
      const isCurrentPublishedVersion =
        version.status === "PUBLISHED" &&
        version.versionCode === meta?.latestPublishedVersionCode;
      return (
        <div className="d-flex align-items-center gap-2">
          <span>{version.versionName}</span>
          {isCurrentPublishedVersion ? (
            <Badge bg="success">Current</Badge>
          ) : null}
        </div>
      );
    },
    minSize: 120,
  },
  {
    accessorKey: "versionCode",
    header: () => "Version Code",
    minSize: 90,
    maxSize: 120,
  },
  {
    accessorKey: "status",
    header: () => "Status",
    minSize: 100,
    maxSize: 130,
  },
  {
    accessorKey: "fileName",
    header: () => "Firmware file",
    cell: ({ row, table }) => {
      const version = row.original;
      const meta = table.options.meta as FirmwareVersionsTableMeta;
      return (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            meta?.onDownload?.(version);
          }}
          style={{ cursor: "pointer" }}
        >
          {version.fileName}
        </a>
      );
    },
    minSize: 220,
  },
  {
    accessorKey: "fileSize",
    header: () => "Size",
    cell: (info) => formatFileSize(info.getValue<number>()),
    minSize: 80,
    maxSize: 100,
  },
  {
    accessorKey: "forceUpdate",
    header: () => "Force Update",
    cell: (info) => (info.getValue<boolean>() ? "Yes" : "No"),
    minSize: 100,
    maxSize: 120,
  },
  {
    accessorKey: "releaseNotes",
    header: () => "Release Notes",
    minSize: 180,
  },
  {
    accessorKey: "sha256",
    header: () => "SHA256",
    cell: (info) => <code>{info.getValue<string>()}</code>,
    minSize: 220,
  },
  {
    accessorKey: "createdAt",
    header: () => "Created",
    cell: (info) => <ActivityDate date={info.getValue<string>()} />,
    minSize: 120,
    maxSize: 150,
  },
];

export function FirmwareVersionsTable({
  versions,
  totalVersions,
  onEdit,
  onDelete,
  onPublish,
  onDownload,
  onTableChange,
  pageIndex,
  pageSize,
  sortBy,
  sortDescending,
  showActions = true,
}: FirmwareVersionsTableProps): React.ReactNode {
  const columns = useMemo(
    () => (showActions ? [...baseColumns, actionsColumn] : baseColumns),
    [showActions]
  );

  const latestPublishedVersionCode = versions
    .filter((version) => version.status === "PUBLISHED")
    .reduce<
      number | undefined
    >((latestCode, version) => (latestCode === undefined || version.versionCode > latestCode ? version.versionCode : latestCode), undefined);

  const table = useReactTable<FirmwareVersionDTO>({
    data: versions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (pagination) => {
      onTableChange(pagination, (prev) => prev);
    },
    onSortingChange: (sorting) => {
      onTableChange((prev) => ({ ...prev, pageIndex: 0 }), sorting);
    },
    enableColumnResizing: true,
    meta: {
      onEdit,
      onDelete,
      onPublish,
      onDownload,
      latestPublishedVersionCode,
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: totalVersions,
    state: {
      pagination: { pageIndex, pageSize },
      sorting: sortBy
        ? [
            {
              id: sortBy,
              desc: sortDescending || false,
            },
          ]
        : [],
    },
    defaultColumn: {
      size: Number.NaN,
      minSize: Number.NaN,
      maxSize: Number.NaN,
    },
  });

  return <DataTable table={table} itemName="firmware version" />;
}
